/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';
import { msGraphApi } from '../msGraph/msGraphApi';
import { MSG_TYPE } from '@/utils/folders';
import { getDownloadURL, getMetadata, listAll, ref } from 'firebase/storage';
import { storage } from '@/utils/firebase';
import MsgReaderModule from '@kenjiuno/msgreader';
import { msgPickDate } from '@/utils/constants';
import PostalMime from 'postal-mime';
import { safelyParseDate } from '@/utils/date';
import { graphClient } from '@/utils/msGraphClient';
import { EML_TYPE } from '@/utils/folders';


const isEmailFile = (file) =>
	file?.file?.mimeType === MSG_TYPE || file?.file?.mimeType === EML_TYPE;

const patchMsGraphCacheWithMsgMetadata = (
	dispatch,
	{ id, driveId, clientFolderDriveId },
	metadata,
) => {
	const { sentDate, toEmail, receivedDate, fromEmail } = metadata;
	try {
		dispatch(
			msGraphApi.util.updateQueryData(
				'getFoldersAndFiles',
				{
					itemId: driveId,
					driveId: clientFolderDriveId,
					recursive: true,
				},
				(draft) => {
					if (draft.files) {
						draft.files = draft.files.map((f) => {
							if (!isEmailFile(f) || f?.id !== id) return f;
							//Create generic sort date for emails
							const sortDate = safelyParseDate(sentDate)?.getTime() ?? f.sortDate;
							return {
								...f,
								sentDate,
								toEmail,
								receivedDate,
								fromEmail,
								...(sortDate ? { sortDate } : {}),
							};
						});
					}
				},
			),
		);
	} catch (e) {
		console.warn('Could not update the cache with the msg file metadata', e);
	}
};

// Use Graph /content to avoid CORS errors when fetching SharePoint files
// from the portal instead of using the file's downloadUrl directly.
const fetchSharepointFile = async ({ driveId, itemId }) => {
	try {
		const data = await graphClient
			.api(`/drives/${driveId}/items/${itemId}/content`)
			.responseType('arraybuffer')
			.get();
		return { data };
	} catch (error) {
		return { error };
	}
};
export const filesApi = createApi({
	reducerPath: 'filesApi',
	tagTypes: ['FILES', 'FILES_LIST', 'NOTE_FILES'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		// Defining an endpoint to get invoices
		getMsgFileHtml: builder.query({
			providesTags: (result, error, { id, driveId, clientFolderDriveId }) => [
				{ type: 'FILES', id },
				{ type: 'FILES_LIST', id: `${clientFolderDriveId}-${driveId}` },
			],
			query: ({ id, downloadUrl, driveId, clientFolderDriveId, createdDateTime }) => ({
				url: process.env.REACT_APP_GET_MSG_FILE_HTML,
				method: 'POST',
				body: {
					downloadUrl,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform getMsgFileHtml response', response);
				return response.data;
			},
			onQueryStarted: async (
				{ id, driveId, clientFolderDriveId, createdDateTime },
				{ dispatch, queryFulfilled },
			) => {
				try {
					const res = await queryFulfilled;
					const date =
						res?.data?.messageDeliveryTime ??
						res?.data?.clientSubmitTime ??
						res?.data?.date ??
						res?.data?.creationTime ??
						createdDateTime;
					const sortDate = safelyParseDate(date)?.getTime();
					if (sortDate) {
						dispatch(
							msGraphApi.util.updateQueryData(
								'getFoldersAndFiles',
								{
									itemId: driveId,
									driveId: clientFolderDriveId,
									recursive: true,
								},
								(draft) => {
									if (draft.files) {
										draft.files = draft.files.map((f) =>
											isEmailFile(f) && f?.id === id
												? {
														...f,
														sortDate,
													}
												: f,
										);
									}
								},
							),
						);
					}
				} catch (e) {
					// patchResult.undo();
				}
			},
		}),
		getMsgFileMetadata: builder.query({
			providesTags: (result, error, { id, driveId, clientFolderDriveId }) => [
				{ type: 'FILES', id },
				{ type: 'FILES_LIST', id: `${clientFolderDriveId}-${driveId}` },
			],
			queryFn: async ({ id, driveId, clientFolderDriveId }, { dispatch }) => {
				const { data: arrayBuffer, error } = await fetchSharepointFile({
					driveId: clientFolderDriveId,
					itemId: id,
				});
				if (error) {
					console.error('❌ Failed to fetch file', error);
					return { error };
				}

				const MsgReader = MsgReaderModule.default || MsgReaderModule;
				const reader = new MsgReader(arrayBuffer);
				const info = reader.getFileData();

				const sentDate = msgPickDate(
					info.clientSubmitTime || info.messageDeliveryTime || info.sentOn || info.date,
				);

				const receivedDate = msgPickDate(
					info.messageDeliveryTime || info.deliveryTime || info.receivedOn,
				);
				const fromEmail =
					info.senderSmtpAddress ?? info.creatorSMTPAddress ?? info?.inetAcctName;
				const recipients = info.recipients ?? [];

				const sortedRecipients = [
					...recipients.filter((r) => r.recipType === 'to'),
					...recipients.filter((r) => r.recipType !== 'to'),
				];

				const toEmail = sortedRecipients.map((r) => r.smtpAddress).join(', ');

				const data = {
					sentDate,
					toEmail,
					receivedDate,
					fromEmail,
				};
				patchMsGraphCacheWithMsgMetadata(
					dispatch,
					{ id, driveId, clientFolderDriveId },
					data,
				);

				return {
					data,
				};
			},
		}),
		getEmlFileMetadata: builder.query({
			providesTags: (result, error, { id, driveId, clientFolderDriveId }) => [
				{ type: 'FILES', id },
				{ type: 'FILES_LIST', id: `${clientFolderDriveId}-${driveId}` },
			],
			queryFn: async ({ id, driveId, clientFolderDriveId }, { dispatch }) => {
				const { data: arrayBuffer, error } = await fetchSharepointFile({
					driveId: clientFolderDriveId,
					itemId: id,
				});
				if (error) {
					console.error('❌ Failed to fetch file', error);
					return { error };
				}

				const info = await PostalMime.parse(arrayBuffer);

				const sentDate = msgPickDate(
					info.date || info.headers?.find((d) => d.key === 'date')?.value,
				);

				const receivedDate = sentDate;

				const fromEmail =
					info?.from?.address ?? info.headers?.find((d) => d.key === 'from')?.value;

				const recipients = info.to;

				const toEmail =
					recipients?.map((r) => r.address)?.join(', ') ??
					info.headers?.find((d) => d.key === 'to')?.value ??
					'';

				const data = {
					sentDate,
					toEmail,
					receivedDate,
					fromEmail,
				};
				patchMsGraphCacheWithMsgMetadata(
					dispatch,
					{ id, driveId, clientFolderDriveId },
					data,
				);

				return {
					data,
				};
			},
		}),
		getNoteFiles: builder.query({
			providesTags: (result, error, { noteId }) => [{ type: 'NOTE_FILES', noteId }],
			queryFn: async (
				{ folderPath, noteId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const storageRef = ref(storage, folderPath);
					const fileList = await listAll(storageRef);

					// Retrieve metadata for each file and filter results
					const matchingFiles = await Promise.all(
						fileList.items.map(async (fileRef) => {
							const metadata = await getMetadata(fileRef);
							const downloadUrl = await getDownloadURL(fileRef);

							return metadata.customMetadata?.['Note ID'] === noteId.toString()
								? {
										...metadata,
										name: fileRef.name,
										path: fileRef.fullPath,
										downloadUrl,
									}
								: null;
						}),
					);

					return { data: matchingFiles.filter((f) => f) };
				} catch (error) {
					console.error(`❌ Error retrieving note ${noteId} related files`, error);

					return {
						error: { code: error?.code, message: error?.message },
					};
				}
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetMsgFileHtmlQuery,
	useGetNoteFilesQuery,
	useGetEmlFileMetadataQuery,
	useGetMsgFileMetadataQuery,
} = filesApi;
